import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
    Loader2, LogOut, Store, Sparkles, Wrench, ShieldCheck, ArrowRight, Clock,
    Camera, Upload, X, Image as ImageIcon,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { SERVICES, SIZES, VEHICLES, formatPrice } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pp_shop_token";

async function call(path, { method = "GET", body, token } = {}) {
    const res = await fetch(`${API}${path}`, {
        method,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Something went wrong");
    return data;
}

export default function ShopPortal() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);

    const saveAuth = useCallback((tok, shopObj) => {
        localStorage.setItem(TOKEN_KEY, tok);
        setToken(tok);
        setShop(shopObj);
    }, []);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setShop(null);
    };

    // Google OAuth callback: exchange session_id in URL hash for a shop token
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    useEffect(() => {
        const hash = window.location.hash;
        if (hash?.includes("session_id=")) {
            const sid = hash.split("session_id=")[1].split("&")[0];
            window.history.replaceState(null, "", window.location.pathname);
            call("/shops/auth/google", { method: "POST", body: { session_id: sid } })
                .then((d) => {
                    saveAuth(d.token, d.shop);
                    toast.success("Signed in with Google");
                })
                .catch((e) => toast.error(e.message))
                .finally(() => setLoading(false));
        }
    }, [saveAuth]);

    useEffect(() => {
        if (window.location.hash?.includes("session_id=")) return;
        if (!token) {
            setLoading(false);
            return;
        }
        call("/shops/me", { token })
            .then(setShop)
            .catch(() => logout())
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen bg-carbon grid place-items-center">
                <Loader2 className="animate-spin text-electric" size={32} />
            </div>
        );
    }

    if (!shop) return <AuthScreen onAuthed={saveAuth} />;

    return <Dashboard shop={shop} token={token} setShop={setShop} onLogout={logout} />;
}

/* ---------------- Auth ---------------- */
function AuthScreen({ onAuthed }) {
    const [mode, setMode] = useState("login");
    const [f, setF] = useState({ email: "", password: "", shop_name: "", contact_name: "", phone: "", city: "", region: "" });
    const [busy, setBusy] = useState(false);
    const up = (k, v) => setF((s) => ({ ...s, [k]: v }));

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/shops";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const submit = async () => {
        setBusy(true);
        try {
            const path = mode === "login" ? "/shops/login" : "/shops/register";
            const body = mode === "login"
                ? { email: f.email, password: f.password }
                : f;
            const d = await call(path, { method: "POST", body });
            onAuthed(d.token, d.shop);
            toast.success(mode === "login" ? "Welcome back" : "Account created — pending approval");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-carbon relative grid place-items-center px-6 py-12 overflow-hidden">
            <div className="ai-grid absolute inset-0 pointer-events-none" aria-hidden="true" />
            <div className="relative w-full max-w-md glass rounded-3xl p-8 shadow-glow-cyan" data-testid="shop-auth-card">
                <div className="flex justify-center mb-6"><Logo light /></div>
                <div className="inline-flex w-full rounded-xl border border-white/10 overflow-hidden mb-6">
                    {["login", "register"].map((m) => (
                        <button key={m} data-testid={`shop-tab-${m}`} onClick={() => setMode(m)}
                            className={`flex-1 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors ${mode === m ? "bg-safety text-white font-semibold" : "text-slate-300 hover:bg-white/5"}`}>
                            {m === "login" ? "Sign In" : "Register Shop"}
                        </button>
                    ))}
                </div>

                <button onClick={googleLogin} data-testid="shop-google-btn"
                    className="w-full h-12 rounded-xl bg-white text-slate-800 font-semibold inline-flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors mb-4">
                    <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
                </button>
                <div className="text-center text-xs text-slate-500 mb-4">or use email</div>

                <div className="space-y-3">
                    <Input label="Email" value={f.email} onChange={(v) => up("email", v)} type="email" testid="shop-email" />
                    <Input label="Password" value={f.password} onChange={(v) => up("password", v)} type="password" testid="shop-password" />
                    {mode === "register" && (
                        <>
                            <Input label="Shop name" value={f.shop_name} onChange={(v) => up("shop_name", v)} testid="shop-name" />
                            <div className="grid grid-cols-2 gap-3">
                                <Input label="City" value={f.city} onChange={(v) => up("city", v)} testid="shop-city" />
                                <Input label="Region" value={f.region} onChange={(v) => up("region", v)} testid="shop-region" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Input label="Contact name" value={f.contact_name} onChange={(v) => up("contact_name", v)} testid="shop-contact" />
                                <Input label="Phone" value={f.phone} onChange={(v) => up("phone", v)} testid="shop-phone" />
                            </div>
                        </>
                    )}
                    <button onClick={submit} disabled={busy} data-testid="shop-auth-submit"
                        className="w-full h-12 rounded-xl bg-safety hover:bg-safety-dark text-white font-bold uppercase tracking-[0.15em] text-sm shadow-glow-orange transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : (mode === "login" ? "Sign In" : "Create Account")}
                    </button>
                </div>
                <a href="/" className="block text-center text-xs text-slate-500 hover:text-electric mt-5 transition-colors">← Back to Panel Pros</a>
            </div>
        </div>
    );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ shop, token, setShop, onLogout }) {
    const [tab, setTab] = useState("quote");
    const approved = shop.status === "approved";
    return (
        <div className="min-h-screen bg-carbon text-slate-100">
            <header className="border-b border-white/10 bg-carbon/80 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-6 h-18 py-4 flex items-center justify-between">
                    <Logo light />
                    <div className="flex items-center gap-4">
                        <span className="hidden sm:flex items-center gap-2 text-sm text-slate-300">
                            <Store size={16} className="text-electric" /> {shop.shop_name}
                        </span>
                        <StatusBadge status={shop.status} />
                        <button onClick={onLogout} data-testid="shop-logout" className="text-slate-400 hover:text-safety transition-colors inline-flex items-center gap-1.5 text-sm">
                            <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8">
                {!approved && (
                    <div data-testid="pending-banner" className="mb-6 glass rounded-2xl p-4 border border-amber-500/30 flex items-center gap-3">
                        <Clock size={18} className="text-amber-400 shrink-0" />
                        <p className="text-sm text-slate-300">
                            Your shop is <span className="text-amber-400 font-semibold">pending approval</span>. You can set your pricing now; the AI quoting tool unlocks once Panel Pros approves you as a preferred supplier for your city.
                        </p>
                    </div>
                )}

                <div className="flex gap-2 mb-6">
                    {[["quote", "AI Quoting", Sparkles], ["pricing", "My Pricing", Wrench], ["profile", "Shop Details", Store]].map(([k, label, Icon]) => (
                        <button key={k} data-testid={`shop-tab-nav-${k}`} onClick={() => setTab(k)}
                            className={`px-4 py-2 rounded-xl text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 transition-colors ${tab === k ? "bg-safety text-white font-semibold" : "text-slate-300 border border-white/10 hover:border-electric/50"}`}>
                            <Icon size={14} /> {label}
                        </button>
                    ))}
                </div>

                {tab === "quote" && <QuoteTool token={token} approved={approved} />}
                {tab === "pricing" && <PricingEditor shop={shop} token={token} setShop={setShop} />}
                {tab === "profile" && <ProfileEditor shop={shop} token={token} setShop={setShop} />}
            </main>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        approved: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
        pending: "text-amber-300 border-amber-500/40 bg-amber-500/10",
        suspended: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    };
    return <span data-testid="shop-status" className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-xl border ${map[status] || map.pending}`}>{status}</span>;
}

/* ---------------- AI Quote tool ---------------- */
const serviceLabel = (key) => SERVICES.find((s) => s.key === key)?.label || key;

function QuoteTool({ token, approved }) {
    const [mode, setMode] = useState("photo"); // "photo" | "detailed"
    const [q, setQ] = useState({ services: [], damage_size: "medium", vehicle_type: "sedan", labour_hours: 2, parts_cost: "", premium_paint: false, description: "" });
    const [photo, setPhoto] = useState(null); // {b64, mime, preview}
    const [pq, setPq] = useState({ vehicle_type: "sedan", premium_paint: false, labour_hours: "", parts_cost: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null); // {..., _mode}
    const inputRef = useRef(null);

    const toggle = (key) => setQ((s) => ({ ...s, services: s.services.includes(key) ? s.services.filter((x) => x !== key) : [...s.services, key] }));

    const handleFile = (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Please upload a JPG, PNG, or WEBP image.");
        if (file.size > 6 * 1024 * 1024) return toast.error("Image must be under 6 MB.");
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result);
            setPhoto({ b64: dataUrl.split(",")[1], mime: file.type, preview: dataUrl });
            setResult(null);
        };
        reader.readAsDataURL(file);
    };

    const runDetailed = async () => {
        if (!q.services.length) return toast.error("Pick at least one service");
        setBusy(true);
        try {
            const body = { ...q, labour_hours: Number(q.labour_hours) || 0, parts_cost: q.parts_cost === "" ? null : Number(q.parts_cost) };
            const d = await call("/shops/me/quote", { method: "POST", body, token });
            setResult({ ...d, _mode: "detailed" });
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    const runPhoto = async () => {
        if (!photo) return toast.error("Add a photo of the damage first.");
        setBusy(true);
        try {
            const body = {
                photo_base64: photo.b64,
                vehicle_type: pq.vehicle_type,
                premium_paint: pq.premium_paint,
                labour_hours: pq.labour_hours === "" ? null : Number(pq.labour_hours),
                parts_cost: pq.parts_cost === "" ? null : Number(pq.parts_cost),
                notes: pq.notes,
            };
            const d = await call("/shops/me/quote/photo", { method: "POST", body, token });
            setResult({ ...d, _mode: "photo" });
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    const refineInDetailed = () => {
        if (!result || result._mode !== "photo") return;
        setQ({
            services: result.detected_services || [],
            damage_size: result.detected_damage_size || "medium",
            vehicle_type: pq.vehicle_type,
            labour_hours: result.labour_hours_used ?? 2,
            parts_cost: result.parts_cost_used ? String(result.parts_cost_used) : "",
            premium_paint: pq.premium_paint,
            description: pq.notes || "",
        });
        setResult(null);
        setMode("detailed");
        toast.success("Loaded into the detailed quote — fine-tune and regenerate.");
    };

    if (!approved) {
        return <div className="glass rounded-2xl p-8 text-center text-slate-400" data-testid="quote-locked">The AI quoting tool unlocks once your shop is approved.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="inline-flex rounded-xl border border-white/10 overflow-hidden" data-testid="shop-quote-mode-toggle">
                {[["photo", "Photo estimate", Camera], ["detailed", "Detailed quote", Wrench]].map(([m, label, Icon]) => (
                    <button key={m} data-testid={`shop-quote-mode-${m}`} onClick={() => { setMode(m); setResult(null); }}
                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 transition-colors ${mode === m ? "bg-safety text-white font-semibold" : "text-slate-300 hover:bg-white/5"}`}>
                        <Icon size={14} /> {label}
                    </button>
                ))}
            </div>

            <div className="grid lg:grid-cols-5 gap-6">
                {/* ---- Form column ---- */}
                {mode === "detailed" ? (
                    <div className="lg:col-span-3 glass rounded-3xl p-6 space-y-5" data-testid="shop-quote-form">
                        <div>
                            <FieldLabel>Services</FieldLabel>
                            <div className="grid grid-cols-2 gap-2">
                                {SERVICES.map((s) => (
                                    <button key={s.key} data-testid={`shop-q-service-${s.key}`} onClick={() => toggle(s.key)}
                                        className={`text-left px-3 py-2.5 rounded-xl border text-sm transition-colors ${q.services.includes(s.key) ? "border-electric bg-electric/10 text-white" : "border-white/10 bg-carbon2 text-slate-300 hover:border-electric/40"}`}>
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Select label="Damage size" value={q.damage_size} onChange={(v) => setQ((s) => ({ ...s, damage_size: v }))} options={SIZES.map((x) => [x.key, x.label])} testid="shop-q-size" />
                            <Select label="Vehicle" value={q.vehicle_type} onChange={(v) => setQ((s) => ({ ...s, vehicle_type: v }))} options={VEHICLES.map((x) => [x.key, x.label])} testid="shop-q-vehicle" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Labour hours" type="number" value={q.labour_hours} onChange={(v) => setQ((s) => ({ ...s, labour_hours: v }))} testid="shop-q-hours" />
                            <Input label="Parts cost NZD (blank = AI estimates)" type="number" value={q.parts_cost} onChange={(v) => setQ((s) => ({ ...s, parts_cost: v }))} testid="shop-q-parts" />
                        </div>
                        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
                            <input type="checkbox" data-testid="shop-q-premium" checked={q.premium_paint} onChange={(e) => setQ((s) => ({ ...s, premium_paint: e.target.checked }))} className="accent-safety w-4 h-4" />
                            Premium paint (metallic / pearl / tri-coat)
                        </label>
                        <Input label="Notes (optional)" value={q.description} onChange={(v) => setQ((s) => ({ ...s, description: v }))} testid="shop-q-notes" />
                        <button onClick={runDetailed} disabled={busy} data-testid="shop-q-run"
                            className="w-full h-12 rounded-xl bg-safety hover:bg-safety-dark text-white font-bold uppercase tracking-[0.15em] text-sm shadow-glow-orange transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <><Sparkles size={16} /> Generate Quote</>}
                        </button>
                    </div>
                ) : (
                    <div className="lg:col-span-3 glass rounded-3xl p-6 space-y-5" data-testid="shop-photo-form">
                        {!photo ? (
                            <div data-testid="shop-photo-dropzone" className="border-2 border-dashed border-electric/40 bg-carbon2/60 p-8 text-center rounded-2xl">
                                <div className="mx-auto w-14 h-14 rounded-2xl bg-electric/10 flex items-center justify-center"><Camera size={26} strokeWidth={1.5} className="text-electric" /></div>
                                <div className="mt-3 font-display text-xl text-white">Add a photo of the damage</div>
                                <p className="text-slate-400 text-sm mt-1.5">Our AI reads the damage, then prices it on <b>your</b> workshop rates — JPG, PNG, WEBP up to 6 MB</p>
                                <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
                                    <label data-testid="shop-photo-camera" className="w-full sm:w-auto cursor-pointer px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white bg-safety hover:bg-safety-dark transition-all shadow-glow-orange rounded-xl inline-flex items-center justify-center gap-2">
                                        <Camera size={16} /> Take a Photo
                                        <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" data-testid="shop-photo-camera-input" onChange={(e) => handleFile(e.target.files?.[0])} />
                                    </label>
                                    <label data-testid="shop-photo-upload" className="w-full sm:w-auto cursor-pointer px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white bg-white/5 border border-white/15 hover:border-electric/60 hover:bg-white/10 transition-all rounded-xl inline-flex items-center justify-center gap-2">
                                        <Upload size={16} className="text-electric" /> Upload a Photo
                                        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="shop-photo-input" onChange={(e) => handleFile(e.target.files?.[0])} />
                                    </label>
                                </div>
                            </div>
                        ) : (
                            <div className="relative">
                                <img src={photo.preview} alt="Damage" data-testid="shop-photo-preview" className="w-full max-h-64 object-cover rounded-2xl border border-white/10" />
                                <button onClick={() => { setPhoto(null); setResult(null); }} data-testid="shop-photo-remove" className="absolute top-2 right-2 glass hover:bg-safety hover:text-white p-2 rounded-xl text-slate-200 transition-colors" aria-label="Remove photo"><X size={16} /></button>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <Select label="Vehicle" value={pq.vehicle_type} onChange={(v) => setPq((s) => ({ ...s, vehicle_type: v }))} options={VEHICLES.map((x) => [x.key, x.label])} testid="shop-pq-vehicle" />
                            <Input label="Labour hours (blank = AI suggests)" type="number" value={pq.labour_hours} onChange={(v) => setPq((s) => ({ ...s, labour_hours: v }))} testid="shop-pq-hours" />
                        </div>
                        <Input label="Parts cost NZD (blank = AI estimates)" type="number" value={pq.parts_cost} onChange={(v) => setPq((s) => ({ ...s, parts_cost: v }))} testid="shop-pq-parts" />
                        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
                            <input type="checkbox" data-testid="shop-pq-premium" checked={pq.premium_paint} onChange={(e) => setPq((s) => ({ ...s, premium_paint: e.target.checked }))} className="accent-safety w-4 h-4" />
                            Premium paint (metallic / pearl / tri-coat)
                        </label>
                        <Input label="Notes (optional)" value={pq.notes} onChange={(v) => setPq((s) => ({ ...s, notes: v }))} testid="shop-pq-notes" />
                        <button onClick={runPhoto} disabled={busy} data-testid="shop-pq-run"
                            className="w-full h-12 rounded-xl bg-safety hover:bg-safety-dark text-white font-bold uppercase tracking-[0.15em] text-sm shadow-glow-orange transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
                            {busy ? <><Loader2 size={16} className="animate-spin" /> Reading your photo...</> : <><Sparkles size={16} /> Estimate from Photo</>}
                        </button>
                    </div>
                )}

                {/* ---- Result column ---- */}
                <div className="lg:col-span-2">
                    {result ? (
                        <div className="glass rounded-3xl p-6 shadow-glow-cyan" data-testid="shop-quote-result">
                            <div className="text-xs uppercase tracking-[0.2em] text-electric mb-2">Your quote</div>
                            <div className="font-display font-bold text-3xl text-white" data-testid="shop-quote-range">
                                {formatPrice(result.low)}<span className="text-slate-500 mx-1.5">–</span>{formatPrice(result.high)}
                            </div>
                            <p className="text-slate-300 text-sm mt-3">{result.summary}</p>

                            {result._mode === "photo" && result.detected_services?.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-2" data-testid="shop-quote-detected">
                                    {result.detected_services.map((s) => (
                                        <span key={s} className="px-2.5 py-1 bg-white/10 border border-white/20 text-slate-200 text-xs rounded-full">{serviceLabel(s)}</span>
                                    ))}
                                    <span className="px-2.5 py-1 bg-white/10 border border-white/20 text-slate-200 text-xs rounded-full capitalize">Size: {result.detected_damage_size}</span>
                                </div>
                            )}

                            <div className="mt-4 space-y-2">
                                {result.breakdown.map((b) => (
                                    <div key={b.label} className="flex justify-between text-sm border-b border-white/10 pb-1.5">
                                        <span className="text-slate-400">{b.label}</span>
                                        <span className="text-white">{formatPrice(b.amount)}</span>
                                    </div>
                                ))}
                            </div>

                            {result._mode === "photo" && result.labour_hours_estimated_by_ai && (
                                <p className="text-xs text-electric mt-2">Labour of {result.labour_hours_used} hr estimated by AI — adjust it in the detailed quote if needed.</p>
                            )}
                            {result.parts_estimated_by_ai && result.parts_cost_used > 0 && (
                                <p className="text-xs text-electric mt-2">Parts cost of {formatPrice(result.parts_cost_used)} estimated by AI — override it if you have a supplier quote.</p>
                            )}
                            {result.recommendations?.length > 0 && (
                                <ul className="mt-4 space-y-1.5">
                                    {result.recommendations.map((r, i) => (
                                        <li key={i} className="flex gap-2 text-xs text-slate-400"><Sparkles size={13} className="text-electric mt-0.5 shrink-0" /><span>{r}</span></li>
                                    ))}
                                </ul>
                            )}

                            {result._mode === "photo" && (
                                <button onClick={refineInDetailed} data-testid="shop-quote-refine"
                                    className="w-full mt-5 h-11 rounded-xl bg-white/5 border border-white/20 text-slate-200 hover:bg-white/10 hover:border-electric/50 text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2">
                                    <Wrench size={15} /> Refine in detailed form
                                </button>
                            )}
                            <p className="text-xs text-slate-500 mt-4">Built from your workshop rates · excl. GST</p>
                        </div>
                    ) : (
                        <div className="glass rounded-3xl p-8 text-center text-slate-500 text-sm inline-flex flex-col items-center gap-3 w-full">
                            <ImageIcon size={26} className="text-slate-600" />
                            {mode === "photo" ? "Add a photo and we'll read the damage, then price it on your own rates." : "Fill in the job and generate a quote built from your own pricing."}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Pricing editor ---------------- */
function PricingEditor({ shop, token, setShop }) {
    const [p, setP] = useState(shop.pricing);
    const [busy, setBusy] = useState(false);
    const setBase = (k, v) => setP((s) => ({ ...s, service_base: { ...s.service_base, [k]: Number(v) || 0 } }));
    const setVeh = (k, v) => setP((s) => ({ ...s, vehicle_multipliers: { ...s.vehicle_multipliers, [k]: Number(v) || 0 } }));

    const save = async () => {
        setBusy(true);
        try {
            const d = await call("/shops/me/pricing", { method: "PUT", token, body: {
                service_base: p.service_base, labour_rate: Number(p.labour_rate) || 0,
                premium_paint_surcharge_pct: Number(p.premium_paint_surcharge_pct) || 0,
                parts_markup_pct: Number(p.parts_markup_pct) || 0, vehicle_multipliers: p.vehicle_multipliers,
            } });
            setShop({ ...shop, pricing: d.pricing });
            toast.success("Pricing saved");
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    return (
        <div className="glass rounded-3xl p-6 space-y-6" data-testid="shop-pricing">
            <div>
                <FieldLabel>Base price per service (NZD)</FieldLabel>
                <div className="grid sm:grid-cols-2 gap-3">
                    {SERVICES.map((s) => (
                        <Input key={s.key} label={s.label} type="number" value={p.service_base?.[s.key] ?? 0} onChange={(v) => setBase(s.key, v)} testid={`shop-price-${s.key}`} />
                    ))}
                </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
                <Input label="Labour rate (NZD/hr)" type="number" value={p.labour_rate} onChange={(v) => setP((s) => ({ ...s, labour_rate: v }))} testid="shop-labour-rate" />
                <Input label="Premium paint surcharge (%)" type="number" value={p.premium_paint_surcharge_pct} onChange={(v) => setP((s) => ({ ...s, premium_paint_surcharge_pct: v }))} testid="shop-premium-pct" />
                <Input label="Parts markup (%)" type="number" value={p.parts_markup_pct} onChange={(v) => setP((s) => ({ ...s, parts_markup_pct: v }))} testid="shop-parts-markup" />
            </div>
            <div>
                <FieldLabel>Vehicle-type multipliers</FieldLabel>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {Object.keys(p.vehicle_multipliers || {}).map((k) => (
                        <Input key={k} label={k} type="number" value={p.vehicle_multipliers[k]} onChange={(v) => setVeh(k, v)} testid={`shop-veh-${k}`} />
                    ))}
                </div>
            </div>
            <button onClick={save} disabled={busy} data-testid="shop-pricing-save"
                className="h-12 px-8 rounded-xl bg-safety hover:bg-safety-dark text-white font-bold uppercase tracking-[0.15em] text-sm shadow-glow-orange transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Save Pricing"}
            </button>
        </div>
    );
}

function ProfileEditor({ shop, token, setShop }) {
    const [f, setF] = useState({ shop_name: shop.shop_name, contact_name: shop.contact_name || "", phone: shop.phone || "", city: shop.city || "", region: shop.region || "" });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            const d = await call("/shops/me/profile", { method: "PUT", token, body: f });
            setShop(d);
            toast.success("Details saved");
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };
    return (
        <div className="glass rounded-3xl p-6 space-y-4 max-w-xl" data-testid="shop-profile">
            <Input label="Shop name" value={f.shop_name} onChange={(v) => setF((s) => ({ ...s, shop_name: v }))} testid="profile-name" />
            <div className="grid grid-cols-2 gap-3">
                <Input label="City" value={f.city} onChange={(v) => setF((s) => ({ ...s, city: v }))} testid="profile-city" />
                <Input label="Region" value={f.region} onChange={(v) => setF((s) => ({ ...s, region: v }))} testid="profile-region" />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Input label="Contact name" value={f.contact_name} onChange={(v) => setF((s) => ({ ...s, contact_name: v }))} testid="profile-contact" />
                <Input label="Phone" value={f.phone} onChange={(v) => setF((s) => ({ ...s, phone: v }))} testid="profile-phone" />
            </div>
            <p className="text-xs text-slate-500">Email: {shop.email} · Sign-in: {shop.auth_provider}</p>
            <button onClick={save} disabled={busy} data-testid="profile-save"
                className="h-12 px-8 rounded-xl bg-safety hover:bg-safety-dark text-white font-bold uppercase tracking-[0.15em] text-sm shadow-glow-orange transition-all disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Save Details"}
            </button>
        </div>
    );
}

/* ---------------- Small inputs ---------------- */
function FieldLabel({ children }) {
    return <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">{children}</div>;
}
function Input({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400 mb-1.5 capitalize">{label}</div>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full h-11 bg-carbon2 border border-white/15 focus:border-electric focus:outline-none text-white px-3 text-sm rounded-xl" />
        </div>
    );
}
function Select({ label, value, onChange, options, testid }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400 mb-1.5">{label}</div>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full h-11 bg-carbon2 border border-white/15 focus:border-electric focus:outline-none text-white px-3 text-sm rounded-xl">
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </div>
    );
}
